import React from "react";
import { Layers, TrendingUp, TrendingDown } from "lucide-react";
import { scoreTier } from "../lib/api";

export const SessionSummary = ({ items }) => {
  if (!items || items.length < 2) return null;

  const avg = Math.round(items.reduce((s, a) => s + (a.overall_score || 0), 0) / items.length);
  const tier = scoreTier(avg);

  // average per pillar by id
  const map = {};
  items.forEach((a) => {
    (a.report?.pillars || []).forEach((p) => {
      if (!map[p.id]) map[p.id] = { title: p.title, total: 0, n: 0 };
      map[p.id].total += p.score || 0;
      map[p.id].n += 1;
    });
  });
  const pillars = Object.values(map).map((m) => ({ title: m.title, score: Math.round(m.total / m.n) }));

  const scores = items.map((a) => a.overall_score || 0);
  const bestIdx = scores.indexOf(Math.max(...scores));
  const worstIdx = scores.indexOf(Math.min(...scores));

  return (
    <div data-testid="session-summary" className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.04] p-6 mb-6">
      <div className="flex items-center gap-2 mb-5">
        <Layers className="h-4 w-4 text-emerald-400" />
        <h3 className="font-heading font-bold text-slate-100">Session Scorecard</h3>
        <span className="font-mono-tel text-[10px] tracking-widest uppercase text-slate-500 ml-1">
          {items.length} shots reviewed
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
        <div className="lg:col-span-3 flex flex-col items-center">
          <span data-testid="session-avg-score" className="font-mono-tel font-bold text-5xl" style={{ color: tier.color }}>
            {avg}
          </span>
          <span className="font-mono-tel text-[10px] tracking-[0.2em] text-slate-500 uppercase mt-1">Avg score / 100</span>
          <span
            className="mt-2 font-mono-tel text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full"
            style={{ color: tier.color, background: tier.bg, border: `1px solid ${tier.border}` }}
          >
            {tier.label}
          </span>
        </div>

        <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {pillars.map((p) => {
            const t = scoreTier(p.score);
            return (
              <div key={p.title}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-300 truncate">{p.title}</span>
                  <span className="font-mono-tel text-sm font-bold" style={{ color: t.color }}>{p.score}</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${p.score}%`, backgroundColor: t.color }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="lg:col-span-3 flex flex-col gap-2">
          <div className="flex items-center gap-2 text-sm">
            <TrendingUp className="h-4 w-4 text-emerald-400" />
            <span className="text-slate-400">Best:</span>
            <span className="text-slate-200 font-semibold">Shot {bestIdx + 1} · {scores[bestIdx]}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <TrendingDown className="h-4 w-4 text-amber-400" />
            <span className="text-slate-400">Focus:</span>
            <span className="text-slate-200 font-semibold">Shot {worstIdx + 1} · {scores[worstIdx]}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
